import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Users,
  AlertTriangle,
  Cloud,
  Phone,
  Building2,
  Clock,
  Plus,
  Trash2,
  FileText,
  UserCheck,
  Loader2
} from 'lucide-react';
import { api } from '@/utils/api';
// WD-T1: per-personnel PPE check-off + individual sign-off (spec v1.2 item 1).
// WD-T1: per-person PPE check-off + individual sign-off (spec v1.2 item 1).
// v1.5 (Brian 2026-10-03): per-ITEM check-off — line items (model + serial)
// come from GET /jha/ppe-items (the server's gear↔item resolver).
import { collectPpeSignoffIssues, PPE_ITEMS_UNAVAILABLE_BLOCKED, PPE_ITEMS_STILL_LOADING, type JhaPpeCheck, type PpeLineItem } from '@/utils/jhaPpeGear';
import { JhaMemberPpeChecks } from '@/components/JhaMemberPpeChecks';

interface TaskHazardControl {
  id: string;
  task: string;
  hazards: string;
  controls: string;
}

interface CrewMember {
  id: string;
  userId?: number | null;
  name: string;
  credentials: string[];
  signature: string;
  // Qwen audit issue 4: accountless (visitor) signers carry contact fields;
  // account-holder rows leave them undefined and keep the picker UI.
  company?: string;
  phone?: string;
  email?: string;
  // WD-T1: per-person PPE check-offs ({ gearId, notUsed?, results? }).
  ppeChecks?: JhaPpeCheck[];
  // Badge reaudit R4: pre-feature manual rows (no userId, non-guest id, already
  // signed) are marked legacy so the server accepts them as historical
  // 'legacy manual signer' entries — never relabeled or dropped.
  legacy?: boolean;
}

// Credential options
const CREDENTIAL_OPTIONS = [
  'First Aid/CPR',
  'OSHA10',
  'OSHA30',
  'TS&R',
  'Rigging/Signaling',
  'Qualified Electrical Worker',
  'RF Safety',
  'NWSA TT1',
  'NWSA TT2',
  'NWSA TTT',
  'Competent Climber',
  'Competent Rescuer',
  'Authorized Climber',
  'Authorized Rescuer',
];

export function EditJHAPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    workDescription: '',
    scopeOfWork: '',
    elevatedWork: false,
    weather: {
      temperature: '',
      condition: 'Clear',
      windSpeed: '',
    },
    emergencyInfo: {
      is911Functional: true,
      rescuePlan: '',
      hospital: { name: '', address: '', phone: '' },
      clinic: { name: '', address: '', phone: '' },
      police: { name: '', address: '', phone: '', nonEmergency: '' },
      fire: { name: '', address: '', phone: '', nonEmergency: '' },
      utilities: {
        electric: { name: '', phone: '' },
        gas: { name: '', phone: '' },
        water: { name: '', phone: '' },
      },
      evacuationPoint: '',
      communicationPoint: '',
    },
    tasks: [] as TaskHazardControl[],
    crewMembers: [] as CrewMember[],
    siteVisitors: [] as string[],
    multiEmployerAcknowledged: false,
  });

  const [projectName, setProjectName] = useState('');
  const [projectId, setProjectId] = useState<number | null>(null);
  // Badge reaudit R3: same company-scoped account list CreateJHAPage uses.
  const [users, setUsers] = useState<Array<{ id: number; firstName: string; lastName: string }>>([]);

  useEffect(() => {
    loadJHA();
  }, [id]);

  const loadJHA = async () => {
    try {
      const response = await api.get(`/jha/${id}`);
      const jha = response.data.data;

      setProjectName(jha.project?.name || 'Unknown Project');
      setProjectId(jha.project?.id ?? null);
      // Badge reaudit R3: load the same company-scoped crew-members list the
      // create page uses, so 'Add Person' can link an account (userId).
      api.get('/jha/crew-members')
        .then((res: any) => setUsers(res.data?.data || []))
        .catch(() => setUsers([]));

      // Map JHA data to form state
      setFormData({
        date: jha.date ? String(jha.date).slice(0, 10) : new Date().toISOString().split('T')[0],
        workDescription: jha.workDescription || '',
        scopeOfWork: jha.scopeOfWork || '',
        elevatedWork: jha.elevatedWork || false,
        weather: jha.weather || {
          temperature: '',
          condition: 'Clear',
          windSpeed: '',
        },
        emergencyInfo: jha.emergencyInfo || {
          is911Functional: true,
          rescuePlan: '',
          hospital: { name: '', address: '', phone: '' },
          clinic: { name: '', address: '', phone: '' },
          police: { name: '', address: '', phone: '', nonEmergency: '' },
          fire: { name: '', address: '', phone: '', nonEmergency: '' },
          utilities: {
            electric: { name: '', phone: '' },
            gas: { name: '', phone: '' },
            water: { name: '', phone: '' },
          },
          evacuationPoint: '',
          communicationPoint: '',
        },
        tasks: jha.tasks || [],
        // Badge reaudit R4: mark pre-feature manual rows (no userId, non-guest
        // id, already signed) as legacy so the server keeps them as historical
        // manual-signer entries instead of rejecting the edit.
        crewMembers: (jha.crewMembers || []).map((m: any) =>
          m &&
          typeof m === 'object' &&
          (m.userId == null) &&
          typeof m.id === 'string' &&
          !m.id.startsWith('guest-') &&
          typeof m.signature === 'string' && m.signature.trim() !== ''
            ? { ...m, legacy: true }
            : m
        ),
        siteVisitors: jha.siteVisitors || [],
        multiEmployerAcknowledged: jha.multiEmployerAcknowledged || false,
      });
    } catch (err: any) {
      console.error('Error loading JHA:', err);
      setError(err.response?.data?.message || 'Failed to load JHA');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // WD-T1: for JHAs in the new per-person PPE format, every account-holder
    // entry must keep full check-offs + own signature (mirrors the server).
    const ppeRelevant = formData.crewMembers.some(
      (m: any) => m && typeof m.userId === 'number' && !m.legacy && Array.isArray(m.ppeChecks)
    );
    if (ppeRelevant) {
      // MED-1 (Luna delta-7): a failed gear-list load BLOCKS save with why+how
      // — never a predictable server-side rejection (mirror checks are
      // skipped without the map).
      if (ppeItemsError || ppeItemsLoading) {
        setError(
          'Cannot save yet — ' +
            (ppeItemsLoading ? PPE_ITEMS_STILL_LOADING : PPE_ITEMS_UNAVAILABLE_BLOCKED)
        );
        return;
      }
      const issues = collectPpeSignoffIssues(formData.crewMembers.filter((m: any) => !m.legacy), ppeItems ?? undefined);
      if (issues.length > 0) {
        setError('Cannot save yet — ' + issues.join(' '));
        return;
      }
    }
    const missingVisitor = formData.crewMembers.find(m => isVisitorRow(m) && (!m.name.trim() || !m.email!.trim()));
    if (missingVisitor) {
      setError('Each visitor needs a name and email before saving');
      return;
    }
    setSaving(true);
    setError(null);

    try {
      await api.put(`/jha/${id}`, formData);
      navigate(`/jha/${id}`);
    } catch (err: any) {
      console.error('Error saving JHA:', err);
      // Badge reaudit R3/R4: surface the server's field-specific, actionable
      // error (zod issues array or message) instead of generic text.
      const data = err?.response?.data;
      const zodIssue = Array.isArray(data?.issues) && data.issues.length
        ? `${data.issues[0].path?.join('.') || 'input'}: ${data.issues[0].message}`
        : null;
      setError(zodIssue || data?.message || data?.error || 'Failed to save JHA');
      setSaving(false);
    }
  };

  const addTask = () => {
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, { id: crypto.randomUUID(), task: '', hazards: '', controls: '' }],
    }));
  };

  const removeTask = (taskId: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter(t => t.id !== taskId),
    }));
  };

  const updateTask = (taskId: string, field: keyof TaskHazardControl, value: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => t.id === taskId ? { ...t, [field]: value } : t),
    }));
  };

  const addCrewMember = () => {
    setFormData(prev => ({
      ...prev,
      crewMembers: [...prev.crewMembers, { id: crypto.randomUUID(), name: '', credentials: [], signature: '' }],
    }));
  };

  // Qwen audit issue 4: accountless visitor signer (no user account to pick).
  const addVisitor = () => {
    setFormData(prev => ({
      ...prev,
      crewMembers: [...prev.crewMembers, {
        id: `guest-${Date.now()}`,
        userId: null,
        name: '',
        company: '',
        phone: '',
        email: '',
        credentials: [],
        signature: '',
      }],
    }));
  };

  const isVisitorRow = (m: CrewMember) => m.userId == null && m.email !== undefined;

  const removeCrewMember = (memberId: string) => {
    setFormData(prev => ({
      ...prev,
      crewMembers: prev.crewMembers.filter(m => m.id !== memberId),
    }));
  };

  const updateCrewMember = (memberId: string, field: keyof CrewMember, value: any) => {
    setFormData(prev => ({
      ...prev,
      crewMembers: prev.crewMembers.map(m => m.id === memberId ? { ...m, [field]: value } : m),
    }));
  };

  // v1.5 (Brian 2026-10-03, per-ITEM PPE check-off): each account holder's
  // checklist LINE ITEMS (one per physical piece — model + serial) from the
  // server's gear↔item resolver. null = not loaded (loading/error shown in
  // the checklist); a loaded map may legitimately be empty.
  const [ppeItems, setPpeItems] = useState<Record<number, PpeLineItem[]> | null>(null);
  const [ppeItemsError, setPpeItemsError] = useState<string | null>(null);
  // Luna delta8-2: the gate must also hold while the gear-list request is
  // IN FLIGHT — a fast submit before the resolver returns is unverifiable.
  const [ppeItemsLoading, setPpeItemsLoading] = useState(false);
  const ppeUserIdsKey = Array.from(new Set((formData.crewMembers || [])
    .map((m: any) => (m && typeof m.userId === 'number' && !m.legacy ? m.userId : null))
    .filter((v: any): v is number => typeof v === 'number')))
    .sort((a, b) => a - b)
    .join(',');
  useEffect(() => {
    if (!projectId || ppeUserIdsKey === '') {
      setPpeItems(null);
      setPpeItemsError(null);
      return () => {};
    }
    let cancelled = false;
    setPpeItemsLoading(true);
    api.get(`/jha/ppe-items?projectId=${projectId}&userIds=${ppeUserIdsKey}`)
      .then((res: any) => {
        if (cancelled) return;
        const rows: Array<{ userId: number; items: PpeLineItem[] }> = res.data?.data || [];
        const map: Record<number, PpeLineItem[]> = {};
        for (const row of rows) map[row.userId] = row.items || [];
        setPpeItems(map);
        setPpeItemsError(null);
        setPpeItemsLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setPpeItems(null);
        setPpeItemsError(err?.response?.data?.message || err?.message || 'The gear-list request failed.');
        setPpeItemsLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, ppeUserIdsKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Loading JHA data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-500 text-white">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to={`/jha/${id}`} className="text-amber-100 hover:text-white">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">Edit Pre-Work Hazard Assessment</h1>
                <p className="text-amber-100 mt-1">{projectName}</p>
              </div>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-white text-amber-600 rounded-lg font-semibold hover:bg-amber-50 transition flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mx-6 mt-6">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="space-y-8">
          
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Scope of Work</label>
                <input
                  type="text"
                  value={formData.scopeOfWork}
                  onChange={(e) => setFormData(prev => ({ ...prev, scopeOfWork: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Description</label>
                <textarea
                  value={formData.workDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, workDescription: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="checkbox"
                  id="elevatedWork"
                  checked={formData.elevatedWork}
                  onChange={(e) => setFormData(prev => ({ ...prev, elevatedWork: e.target.checked }))}
                  className="h-4 w-4 text-amber-600 rounded"
                />
                <label htmlFor="elevatedWork" className="text-sm font-medium text-gray-700">Elevated Work</label>
              </div>
            </div>
          </div>

          {/* Weather */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Cloud className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Weather Conditions</h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temperature (°F)</label>
                <input
                  type="text"
                  value={formData.weather.temperature}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    weather: { ...prev.weather, temperature: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                <select
                  value={formData.weather.condition}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    weather: { ...prev.weather, condition: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                >
                  <option>Clear</option>
                  <option>Cloudy</option>
                  <option>Partly Cloudy</option>
                  <option>Rain</option>
                  <option>Snow</option>
                  <option>Fog</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wind Speed (mph)</label>
                <input
                  type="text"
                  value={formData.weather.windSpeed}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    weather: { ...prev.weather, windSpeed: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contacts */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Phone className="h-6 w-6 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">Emergency Contacts</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Hospital */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <h4 className="font-semibold text-red-900 mb-3">🏥 Hospital/ER</h4>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Name"
                    value={formData.emergencyInfo.hospital.name}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyInfo: {
                        ...prev.emergencyInfo,
                        hospital: { ...prev.emergencyInfo.hospital, name: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Address"
                    value={formData.emergencyInfo.hospital.address}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyInfo: {
                        ...prev.emergencyInfo,
                        hospital: { ...prev.emergencyInfo.hospital, address: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Phone"
                    value={formData.emergencyInfo.hospital.phone}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyInfo: {
                        ...prev.emergencyInfo,
                        hospital: { ...prev.emergencyInfo.hospital, phone: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Occupational Clinic */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">🩺 Occupational Clinic</h4>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Name"
                    value={formData.emergencyInfo.clinic.name}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyInfo: {
                        ...prev.emergencyInfo,
                        clinic: { ...prev.emergencyInfo.clinic, name: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Address"
                    value={formData.emergencyInfo.clinic.address}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyInfo: {
                        ...prev.emergencyInfo,
                        clinic: { ...prev.emergencyInfo.clinic, address: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Phone"
                    value={formData.emergencyInfo.clinic.phone}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      emergencyInfo: {
                        ...prev.emergencyInfo,
                        clinic: { ...prev.emergencyInfo.clinic, phone: e.target.value }
                      }
                    }))}
                    className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Evacuation Point</label>
                <input
                  type="text"
                  value={formData.emergencyInfo.evacuationPoint}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    emergencyInfo: { ...prev.emergencyInfo, evacuationPoint: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Communication Point</label>
                <input
                  type="text"
                  value={formData.emergencyInfo.communicationPoint}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    emergencyInfo: { ...prev.emergencyInfo, communicationPoint: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rescue Plan</label>
              <textarea
                value={formData.emergencyInfo.rescuePlan}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  emergencyInfo: { ...prev.emergencyInfo, rescuePlan: e.target.value }
                }))}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {/* Tasks, Hazards & Controls */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <FileText className="h-6 w-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-900">Tasks, Hazards & Controls</h2>
              </div>
              <button
                onClick={addTask}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Task
              </button>
            </div>

            {formData.tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No tasks defined. Click "Add Task" to add one.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.tasks.map((task, index) => (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-gray-700">Task #{index + 1}</span>
                      <button
                        onClick={() => removeTask(task.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Task</label>
                        <input
                          type="text"
                          value={task.task}
                          onChange={(e) => updateTask(task.id, 'task', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Hazards</label>
                        <input
                          type="text"
                          value={task.hazards}
                          onChange={(e) => updateTask(task.id, 'hazards', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Controls</label>
                        <input
                          type="text"
                          value={task.controls}
                          onChange={(e) => updateTask(task.id, 'controls', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Crew Members */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-indigo-600" />
                <h2 className="text-xl font-bold text-gray-900">Project Personnel</h2>
              </div>
              <button
                onClick={addCrewMember}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Person
              </button>
              <button
                type="button"
                onClick={addVisitor}
                className="px-4 py-2 border border-indigo-300 text-indigo-600 rounded-lg hover:bg-indigo-50 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add visitor (no account)
              </button>
            </div>

            {formData.crewMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No crew members. Click "Add Person" to add one.
              </div>
            ) : (
              <div className="space-y-4">
                {formData.crewMembers.map((member, index) => (
                  <div key={member.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <UserCheck className="h-5 w-5 text-indigo-600" />
                        <span className="font-semibold text-gray-700">Person #{index + 1}</span>
                      </div>
                      <button
                        onClick={() => removeCrewMember(member.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">{isVisitorRow(member) ? 'Name *' : 'Name (account)'}</label>
                        {!isVisitorRow(member) && (
                          <select
                            aria-label="Select account holder"
                            value={member.userId || ''}
                            onChange={(e) => {
                              const user = users.find((u) => u.id === parseInt(e.target.value));
                              if (user) {
                                updateCrewMember(member.id, 'userId', user.id);
                                updateCrewMember(member.id, 'name', `${user.firstName} ${user.lastName}`);
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                          >
                            <option value="">Select crew member from your company...</option>
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.firstName} {u.lastName}
                              </option>
                            ))}
                          </select>
                        )}
                        <input
                          type="text"
                          value={member.name}
                          onChange={(e) => updateCrewMember(member.id, 'name', e.target.value)}
                          placeholder={isVisitorRow(member) ? 'Visitor name (required)' : 'Or enter name manually'}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm ${!isVisitorRow(member) ? 'mt-2' : ''}`}
                        />
                        {isVisitorRow(member) && (
                          <div className="mt-2 space-y-2">
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Email *</label>
                              <input
                                type="email"
                                value={member.email || ''}
                                onChange={(e) => updateCrewMember(member.id, 'email', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Company</label>
                              <input
                                type="text"
                                value={member.company || ''}
                                onChange={(e) => updateCrewMember(member.id, 'company', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Phone</label>
                              <input
                                type="tel"
                                value={member.phone || ''}
                                onChange={(e) => updateCrewMember(member.id, 'phone', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Credentials</label>
                        <select
                          multiple
                          value={member.credentials}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, option => option.value);
                            updateCrewMember(member.id, 'credentials', selected);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-24"
                        >
                          {CREDENTIAL_OPTIONS.map(cred => (
                            <option key={cred} value={cred}>{cred}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Signature</label>
                        <input
                          type="text"
                          value={member.signature}
                          onChange={(e) => updateCrewMember(member.id, 'signature', e.target.value)}
                          placeholder="Type name as signature"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-signature"
                        />
                      </div>
                    </div>
                    {/* WD-T1: per-person PPE check-off — account holders only
                        (visitors hold no tracked company gear). */}
                    {!isVisitorRow(member) && !member.legacy && (
                      <JhaMemberPpeChecks
                        member={member}
                        items={ppeItems && member.userId != null ? ppeItems[member.userId] ?? [] : undefined}
                        itemsError={ppeItemsError}
                        onChange={(ppeChecks) => updateCrewMember(member.id, 'ppeChecks', ppeChecks)}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <Link
              to={`/jha/${id}`}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              Cancel
            </Link>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-500 transition flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

